import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLazyGetOnboardingStatusQuery } from 'store/api/customer.apislice';
import {
  useGetCurrentSubscriptionQuery,
  useLazyGetCurrentSubscriptionQuery,
} from 'store/api/subscription.apislice';
import ROUTES from 'router/routes.constants';
import {
  extractOnboardingStatusFromResponse,
  getInvestGateResult,
} from 'utils/investmentEligibility.utils';
import { requestOnboardingPendingPrompt } from 'utils/directJourney.utils';

const GATE_ACTION_MESSAGES = {
  invest: 'investing',
  redeem: 'redeeming',
  switch: 'switching funds',
  stp: 'starting an STP',
  swp: 'starting an SWP',
};

/**
 * Apply the first blocking invest-gate action (navigate, modal, toast).
 * Shared across every Invest entry point so web matches mobile gating.
 */
export const applyInvestGateAction = (gate, navigate, options) => {
  if (!gate || typeof gate !== 'object') return;

  // Callers may pass null/undefined options.
  const { onRiskModal, actionType = 'invest' } =
    options && typeof options === 'object' ? options : {};

  const dispatchModalEvent = (eventName) => {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
      return;
    }
    window.dispatchEvent(new Event(eventName));
  };

  const goToOnboarding = () => {
    if (typeof navigate === 'function') {
      navigate(ROUTES?.ONBOARDING_STATUS || '/onboarding/status');
    }
  };

  switch (gate?.action) {
    case 'onboarding': {
      // Never drop the customer onto onboarding unasked — prompt first, and
      // only move them once they choose Complete.
      const raised = requestOnboardingPendingPrompt({
        pendingStep: gate?.pendingStep ?? null,
        steps: gate?.onboardingSteps ?? null,
        incompleteSteps: gate?.incompleteSteps ?? [],
        actionType,
      });

      // Nothing listening (no host layout / no DOM): keep the old behaviour so
      // the customer is never left with a silently dead Invest button.
      if (!raised) {
        goToOnboarding();
        toast?.error?.(
          `Please complete all onboarding steps before ${GATE_ACTION_MESSAGES?.[actionType] || 'proceeding'}. Incomplete: ${
            gate?.incompleteSteps?.join?.(', ') || 'Onboarding'
          }`,
        );
      }
      break;
    }
    case 'payment':
      dispatchModalEvent('open-plan-selection-modal');
      break;
    case 'agreement':
      dispatchModalEvent('open-agreement-modal');
      break;
    case 'risk':
      if (typeof onRiskModal === 'function') {
        onRiskModal();
      } else {
        dispatchModalEvent('open-risk-assessment-modal');
      }
      break;
    default:
      break;
  }
};

const readSubscriptionActive = (subscriptionResponse) =>
  subscriptionResponse?.result?.status === 'ACTIVE' ||
  subscriptionResponse?.result?.current?.status === 'ACTIVE';

/**
 * Central invest gate for the customer web app.
 * Always refetches onboarding-status on each guard check (no stale snapshots).
 */
export const useInvestGate = () => {
  const navigate = useNavigate();
  const [fetchOnboardingStatus, { isFetching }] =
    useLazyGetOnboardingStatusQuery();
  const { data: subscriptionResponse } = useGetCurrentSubscriptionQuery();
  const [fetchCurrentSubscription] = useLazyGetCurrentSubscriptionQuery();

  const fetchFreshOnboardingStatus = useCallback(async () => {
    const response = await fetchOnboardingStatus(undefined, {
      preferCacheValue: false,
    }).unwrap();

    return extractOnboardingStatusFromResponse(response);
  }, [fetchOnboardingStatus]);

  const checkInvestGate = useCallback(
    async ({ hasExistingHolding } = {}) => {
      // The subscription query is still in flight for the first moments after
      // login, and an unresolved response reads as "unpaid" — which gates a
      // paying Direct customer on 'payment' and opens checkout. Resolve it
      // alongside onboarding instead of trusting whatever the cache holds.
      const [onboardingStatus, subscription] = await Promise.all([
        fetchFreshOnboardingStatus(),
        fetchCurrentSubscription(undefined, false)
          .unwrap()
          .catch(() => subscriptionResponse ?? null),
      ]);

      const gate = getInvestGateResult(onboardingStatus, {
        isSubscriptionActive: readSubscriptionActive(subscription),
        hasExistingHolding,
      });

      return { gate, onboardingStatus };
    },
    [fetchFreshOnboardingStatus, fetchCurrentSubscription, subscriptionResponse],
  );

  /**
   * Runs `proceed` only when the user passes every mandatory step.
   * Pass `hasExistingHolding` for actions on a folio the customer already holds —
   * an existing investor whose KYC went back to KRA review keeps transacting there.
   * Returns true when the invest action proceeded.
   */
  const guardInvest = useCallback(
    async (proceed, options) => {
      // `options` may arrive as null/undefined from any of the Invest entry points.
      const gateOptions =
        options && typeof options === 'object' ? options : {};

      try {
        const gateResult = (await checkInvestGate({
          hasExistingHolding: gateOptions?.hasExistingHolding,
        })) || {};
        const gate = gateResult?.gate ?? null;

        // No gate object means eligibility could not be evaluated - fail closed.
        if (!gate || typeof gate !== 'object' || gate?.allowed !== true) {
          applyInvestGateAction(gate, navigate, gateOptions);
          return false;
        }

        if (typeof proceed !== 'function') return false;

        proceed();
        return true;
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        toast?.error?.(
          error?.data?.message ||
            error?.message ||
            'Failed to check onboarding status',
        );
        if (typeof navigate === 'function') {
          navigate(ROUTES?.ONBOARDING_STATUS || '/onboarding/status');
        }
        return false;
      }
    },
    [checkInvestGate, navigate],
  );

  return {
    guardInvest,
    checkInvestGate,
    isCheckingInvestGate: isFetching,
  };
};
